/**
 * opencode credential + model resolution, shared by the headless harness (cards)
 * and the PTY adapter (adhoc sessions) so the two can never drift on how a
 * deployed pod authenticates.
 *
 * A project's `CodingAgentKey` reaches the pod through the bootstrap bundle
 * (`apps/api` project-key/methods/key-resolution.ts) in one of two shapes:
 *
 *   kind: "chatgpt_oauth"  → CONVEYOR_OPENCODE_OAUTH = base64({access,refresh,expires})
 *       Seeded into opencode's own auth store by `seedOpenCodeOauth`, where the
 *       opencode-openai-codex-auth plugin reads and lazily refreshes it. On GCP
 *       projects that directory is fuse-symlinked, so a refreshed token outlives
 *       the pod.
 *
 *   kind: "api_key"        → CONVEYOR_AGENT_KEY + CONVEYOR_AGENT_PROVIDER
 *       Handed to the child under the env var that provider's SDK reads natively.
 *
 * Neither Conveyor-owned variable is passed through to the child: opencode does
 * not read them, and forwarding a raw key under an unexpected name is a leak with
 * no upside.
 */

import { seedOpenCodeOauth } from "../pty/adapters/opencode-auth.js";

/** provider → the env var its SDK/CLI reads natively. */
const PROVIDER_KEY_ENV: Record<string, string> = {
  openai: "OPENAI_API_KEY",
  anthropic: "ANTHROPIC_API_KEY",
  zen: "OPENCODE_API_KEY",
};

export const DEFAULT_OPENCODE_PROVIDER = "openai";

/**
 * Env overrides that carry the resolved credential to a spawned opencode, plus
 * the removals. Returned rather than applied so it is testable without an env.
 */
export function buildOpenCodeCredentialEnv(source: NodeJS.ProcessEnv): {
  set: Record<string, string>;
  unset: string[];
} {
  const set: Record<string, string> = {};
  // ChatGPT-subscription auth flows through the auth store; a provider API key
  // in the env would override it and silently bill per-token instead.
  const oauthActive = Boolean(source.CONVEYOR_OPENCODE_OAUTH);
  const key = source.CONVEYOR_AGENT_KEY;
  const provider = source.CONVEYOR_AGENT_PROVIDER ?? DEFAULT_OPENCODE_PROVIDER;
  const keyEnvVar = PROVIDER_KEY_ENV[provider];
  if (key && keyEnvVar && !oauthActive) set[keyEnvVar] = key;
  return { set, unset: ["CONVEYOR_AGENT_KEY", "CONVEYOR_OPENCODE_OAUTH"] };
}

/**
 * Prepare on-disk credentials before the first spawn. No-op unless a ChatGPT
 * OAuth seed is present. Best-effort by contract — never throws.
 */
export async function prepareOpenCodeCredentials(
  source: NodeJS.ProcessEnv = process.env,
): Promise<void> {
  await seedOpenCodeOauth(source);
}

/**
 * The full child environment for a spawned `opencode run`: everything inherited,
 * minus Conveyor's own credential variables, plus the resolved credential and the
 * inline config.
 */
export function buildOpenCodeChildEnv(
  source: NodeJS.ProcessEnv,
  configContent: string | null,
): Record<string, string> {
  const env: Record<string, string> = {};
  for (const [key, value] of Object.entries(source)) {
    if (typeof value === "string") env[key] = value;
  }
  const { set, unset } = buildOpenCodeCredentialEnv(source);
  for (const key of unset) delete env[key];
  Object.assign(env, set);
  if (configContent) env.OPENCODE_CONFIG_CONTENT = configContent;
  return env;
}

/**
 * The model to pass as `--model`. opencode expects `provider/model`, so a bare
 * name is qualified with the resolved provider — an unqualified name is not
 * resolved against the configured default and the run fails to pick a model.
 * `CONVEYOR_AGENT_MODEL` (the key's own model) wins over the task's, matching
 * the PTY adapter.
 */
export function resolveOpenCodeModel(
  source: NodeJS.ProcessEnv,
  optionsModel?: string,
): string | undefined {
  const model = source.CONVEYOR_AGENT_MODEL ?? optionsModel;
  if (!model) return undefined;
  if (model.startsWith("zen/")) return `opencode/${model.slice("zen/".length)}`;
  if (model.includes("/")) return model;
  const provider = source.CONVEYOR_AGENT_PROVIDER ?? DEFAULT_OPENCODE_PROVIDER;
  return `${provider === "zen" ? "opencode" : provider}/${model}`;
}
